// This is the V2 layout of StepContentSection. This version includes a heroImage.
// For the version without a heroImage, see StepContentSectionV2NoImage.tsx
// For simplicity, both files share the same typing.

import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import { Row, Col } from "antd";
import {
  FinancialBrand,
  SuccessStepAssets,
  PeopleStepAssets,
} from "@dreambigger/shared/src/types";
import { useSegment } from "@dreambigger/shared/src/hooks";
import { optimizeCloudinaryImg } from "@dreambigger/shared/src/utils";
import animations from "@dreambigger/shared/src/components/board/styles/animations.module.scss";
import { isInAdminMode } from "../AdminApplicationWrapper";
import { SuccessContentV2, StepContentWrapper } from "./index";
export interface StepContentSectionV2Props {
  children: any;
  brand: FinancialBrand;
  title: string;
  subTitle?: string;
  description: string;
  stepProgress: number;
  previousSlug?: string;
  stepType?: string;
  assets?: SuccessStepAssets | PeopleStepAssets;
  hideProgressBar?: boolean;
  heroImage?: string;
  heroText?: string;
  heroLogo?: string;
}

const StepContentSectionV2 = ({
  children,
  brand,
  title,
  subTitle,
  description,
  stepProgress,
  previousSlug,
  stepType,
  assets,
  hideProgressBar,
  heroImage,
  heroText,
  heroLogo,
}: StepContentSectionV2Props) => {
  const router = useRouter();
  const { flowId, stepSlug } = router.query;
  const segment = useSegment();
  const [isSuccessComplete, setIsSuccessComplete] = useState(false);
  const isAdminMode = isInAdminMode();

  const trackAnalytics = () => {
    segment.track({ action: "Logo Click", label: `Logo - ${stepSlug}` });
  };

  useEffect(() => {
    // Check if success is complete after mount to avoid hydration mismatch
    if (stepType === "success" && router.query.submission === "complete") {
      setIsSuccessComplete(true);
    }
  }, [stepType, router.query.submission]);

  const handleClick = () => {
    router.push({
      query: {
        ...router.query,
        flowId,
        stepSlug: previousSlug,
      },
    });
    segment.track({
      action: "Button Click",
      label: `Back - ${stepSlug as string}`,
    });
  };

  const maxWidth = "1800px";

  // SUCCESS STEP - Use separate component
  if (isSuccessComplete) {
    return (
      <SuccessContentV2
        brand={brand}
        title={title}
        subTitle={subTitle}
        description={description}
        assets={assets}
        heroImage={heroImage}
        isAdminMode={isAdminMode}
      >
        {children}
      </SuccessContentV2>
    );
  }

  // REGULAR STEPS - Use existing two-column layout
  return (
    <Row style={{ maxWidth, margin: "0 auto", overflowX: "hidden" }}>
      {" "}
      {/* LEFT COLUMN --- IMAGE SECTION (flipped to right side for success step) */}
      <Col xs={0} lg={12}>
        <div className={`w-100 h-100 pa-6 bg-cta`}>
          {/* Image container */}
          {heroImage && (
            <div
              className={`relative ${animations.fadeIn_f}`}
              style={{
                width: "100%",
                maxWidth: "380px",
                height: "60%",
                maxHeight: "450px",
                marginTop: "15vh",
                marginLeft: "auto",
                marginRight: "auto",
                borderTopRightRadius: "0",
                overflow: "hidden",
              }}
            >
              <Image
                src={optimizeCloudinaryImg(heroImage)}
                alt="Hero"
                layout="fill"
                objectFit="cover"
                objectPosition="center"
                priority={true}
              />
            </div>
          )}

          {/* HeroText Box - Bottom left with rounded top-right corner - only for non-success */}
          {heroText && (
            <div
              className={`flex items-center absolute bottom-0 left-0 bg-primary textPrimary pa-4 ${
                heroText && heroText.length < 300 ? "w-50" : "w-80"
              } `}
              style={{
                borderTopRightRadius: "48px",
              }}
            >
              {heroLogo && (
                <img
                  className="mr-3"
                  src={heroLogo}
                  alt="logo"
                  style={{ maxHeight: "110px" }}
                />
              )}
              <div
                className="mb-0"
                dangerouslySetInnerHTML={{
                  __html: heroText,
                }}
              />
            </div>
          )}
        </div>
      </Col>
      {/* RIGHT COLUMN --- STEP SECTION (Flipped to right for success step) */}
      <Col xs={24} lg={12} className="bg-white">
        <div className="flex flex-column justify-between min-h-100vh">
          <div className="ph-3 md_pl-6 lg_pl-7 xl_pl-7 md_pr-7 lg_pr-8 xl_pr-8 pv-4 lg_pv-6">
            <StepContentWrapper
              brand={brand}
              title={title}
              description={description}
              stepProgress={stepProgress}
              previousSlug={previousSlug}
              hideProgressBar={hideProgressBar}
              handleClick={handleClick}
              trackAnalytics={trackAnalytics}
            >
              {children}
            </StepContentWrapper>
          </div>

          {/* Mobile HeroText - Only visible on mobile, at bottom of screen */}
          {heroText && (
            <div className="flex lg_dn bg-primary textPrimary flex items-center pa-4">
              {heroLogo && (
                <img
                  src={heroLogo}
                  className="mr-4"
                  alt="logo"
                  style={{
                    maxWidth: "60px",
                  }}
                />
              )}
              {heroText && (
                <div
                  className="f-2 lh-2"
                  style={{ margin: 0 }}
                  dangerouslySetInnerHTML={{
                    __html: heroText,
                  }}
                />
              )}
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default StepContentSectionV2;
